import SpektrixAPI from "../utils/SpektrixAPI";
import Cookies from "js-cookie";

class SpektrixSession {
    constructor() {
        this.api = new SpektrixAPI();
        this.loadingIndicator = document.querySelector(
            ".article__loading-indicator",
        );
        this.keepingAlive = false;
    }

    init() {
        window.addEventListener("message", this.receiveMessage.bind(this));
        document.addEventListener("click", this.handleClick.bind(this));
        document.addEventListener(
            "submit",
            this.loginFormSubmission.bind(this),
        );

        this.checkSession();
    }

    /**
     * Check if the session cookie is up to date with the customer session data from Spektrix and
     * set/update it if necessary.
     */
    checkSession() {
        let sessionCookieData = Cookies.get("spektrix_session");
        sessionCookieData = sessionCookieData
            ? JSON.parse(sessionCookieData)
            : {};

        // If there's no session cookie, do nothing (this will be set on login/logout events).
        if (!sessionCookieData.customer) {
            return false;
        }

        this.api.request("/customer", "GET").then((response) => {
            if (response.hasOwnProperty("errorCode")) {
                console.error("Error in response from /customer:", response);
            }

            const email = response.email ?? false;

            if (!email && !sessionCookieData.customer) {
                // If there's no customer logged in and no session cookie, do nothing.
                return;
            }

            if (
                !sessionCookieData.customer ||
                sessionCookieData.customer !== email
            ) {
                // If the Spektrix session email doesn't exist or is different to the cookie,
                // update the session
                this.updateSession(email, false);
            }

            // This is for video platforms and isn't needed here
            // if (sessionCookieData.customer) {
            //     this.keepAlive();
            // }
        });
    }

    receiveMessage(e) {
        if (typeof e.data !== "string") return;

        if (
            e.data.includes("basketHash:NotLoggedIn") ||
            e.data.includes("basketHash:LoggedIn")
        ) {
            this.checkSession();
        }
    }

    async updateSession(
        email = null,
        redirect = false,
        setLoadingState = false,
    ) {
        setLoadingState && this.setLoadingState(true);

        let currentSession = Cookies.get("spektrix_session") ?? "{}";

        currentSession = JSON.parse(currentSession);

        // Run an AJAX request to update the session.
        // If the email is empty, there is no customer logged in so the spektrix_session action will clear
        // the session cookie.
        return fetch(
            `${ajax_client.ajax_url}?action=spektrix_session${
                email
                    ? `&spektrix_customer=${encodeURIComponent(email)}&events=${
                          currentSession.priority_events ?? []
                      }`
                    : ""
            }`,
            {
                method: "GET",
            },
        )
            .then((response) => response.json())
            .then((response) => {
                if (redirect) {
                    const url = new URL(location.href);

                    // If there's a redirect parameter, redirect to that URL.
                    // Otherwise, reload the page if the customer email has been updated.
                    if (url.searchParams.get("r")) {
                        location.href = url.searchParams.get("r");
                    } else if (currentSession.customer !== email) {
                        location.reload();
                    }
                } else {
                    document.dispatchEvent(
                        new CustomEvent("sessionUpdate", {
                            bubbles: true,
                            composed: true,
                        }),
                    );

                    return response;
                }
            })
            .catch((error) => {
                console.error("Error in updateSession:", error);
            });
    }

    async logOut() {
        this.setLoadingState(true);

        const logout = await this.api
            .request("/customer/deauthenticate", "POST", 1)
            .then((response) => {
                this.updateSession(null, true, true);
            })
            .catch((error) => {
                console.error("Error logging out:", error);

                this.setLoadingState(false);
            });
    }

    /**
     * Login to the Spektrix API.
     */
    async login(data, redirect) {
        const _this = this;

        const validation = document.querySelector("[login-validation]");
        const errors = document.querySelector("[login-error]");

        if (errors) errors.textContent = "";
        if (validation) validation.hidden = true;

        /**
         * This is the login request to the Spektrix API.
         * The data object should contain the email and password.
         *
         * @link https://system.spektrix.com/apitesting/api/v3/Help/Api/POST-v3-customer-authenticate?mode=Web
         */
        const login_request = await this.api.request(
            "/customer/authenticate",
            "POST",
            data,
        );

        if (login_request.hasOwnProperty("errorCode")) {
            if (errors)
                errors.textContent =
                    "Login failed. Please check your email address and password and try again.";

            if (validation) validation.hidden = false;

            _this.setLoadingState(false);
        } else {
            _this.updateSession(data.email, redirect, redirect);

            document.dispatchEvent(
                new CustomEvent("loginSuccess", {
                    detail: { user: login_request },
                    bubbles: true,
                    composed: true,
                }),
            );
        }

        return login_request;
    }

    /**
     * Handle the submission of the login form.
     *
     */
    loginFormSubmission(e) {
        if (e.target.id !== "login-form") {
            return;
        }
        e.preventDefault();
        this.setLoadingState(true);

        const form = e.target;
        const formData = new FormData(form);

        const data = {
            email: formData.get("email-address"),
            password: formData.get("password"),
        };

        const redirect = true;

        this.login(data, redirect);
    }

    /**
     * Keep the session alive and our session cookie up to date with Spektrix every 60 seconds.
     */
    keepAlive() {
        if (this.keepingAlive) {
            return;
        }

        setInterval(() => {
            // console.log("Keeping Spektrix session alive...");

            this.checkSession();
        }, 60000); // 1 minute

        this.keepingAlive = true;
    }

    setLoadingState(loading = false) {
        this.loadingIndicator &&
            this.loadingIndicator.setAttribute("active", loading);
    }

    handleClick(e) {
        if (e.target.getAttribute("data-action") === "logout") {
            e.preventDefault();

            // console.log("Logging out...");

            this.logOut();
        }
    }
}

export default SpektrixSession;
